import { useState, useEffect, useCallback } from 'react';
import { useLabsPersistence } from './useLabsPersistence';

// Define the UI state interfaces for different Labs pages
export interface PlaybookEditUIState {
  // Schema preview panel states
  currentSchemaView: 'input' | 'output' | 'yaml';
  currentYamlView: 'playbook' | 'connection';
  isRightPanelCollapsed: boolean;
  rightPanelWidth: number;
  
  // Editor states
  selection: {
    type: 'global' | 'event' | 'connection';
    eventId?: string;
    transformId?: string;
  };
  searchTerm: string;
  viewMode: 'card' | 'table';
}

export interface LabsNavbarUIState {
  isCollapsed: boolean;
}

// Default states
const DEFAULT_PLAYBOOK_EDIT_UI_STATE: PlaybookEditUIState = {
  currentSchemaView: 'input',
  currentYamlView: 'playbook',
  isRightPanelCollapsed: false,
  rightPanelWidth: 480,
  selection: { type: 'global' },
  searchTerm: '',
  viewMode: 'table',
};

const DEFAULT_NAVBAR_UI_STATE: LabsNavbarUIState = {
  isCollapsed: false,
};

/**
 * Hook for managing PlaybookEditPage UI state with persistence
 */
export function usePlaybookEditUI(playbookId?: string) {
  const storageKey = playbookId 
    ? `labs-playbook-edit-ui-state-${playbookId}`
    : 'labs-playbook-edit-ui-state-default';
  
  const { saveState, loadState, clearState, hasPersistedState } = useLabsPersistence<PlaybookEditUIState>(storageKey);
  
  const [uiState, setUIState] = useState<PlaybookEditUIState>(() => {
    const loaded = loadState();
    return loaded || DEFAULT_PLAYBOOK_EDIT_UI_STATE;
  });

  // Save state whenever it changes
  useEffect(() => {
    saveState(uiState);
  }, [uiState, saveState]);

  const updateUIState = useCallback((updates: Partial<PlaybookEditUIState>) => {
    setUIState(prev => ({ ...prev, ...updates }));
  }, []);

  const resetUIState = useCallback(() => {
    setUIState(DEFAULT_PLAYBOOK_EDIT_UI_STATE);
    clearState();
  }, [clearState]);

  return {
    uiState,
    updateUIState,
    resetUIState,
    hasPersistedState: hasPersistedState(),
  };
}

/**
 * Hook for managing Labs Navbar UI state with persistence
 */
export function useLabsNavbarUI() {
  const storageKey = 'labs-navbar-ui-state';
  
  const { saveState, loadState, clearState, hasPersistedState } = useLabsPersistence<LabsNavbarUIState>(storageKey);
  
  const [uiState, setUIState] = useState<LabsNavbarUIState>(() => {
    const loaded = loadState();
    return loaded || DEFAULT_NAVBAR_UI_STATE;
  });

  // Save state whenever it changes
  useEffect(() => {
    saveState(uiState);
  }, [uiState, saveState]);

  const updateUIState = useCallback((updates: Partial<LabsNavbarUIState>) => {
    setUIState(prev => ({ ...prev, ...updates }));
  }, []);

  const resetUIState = useCallback(() => {
    setUIState(DEFAULT_NAVBAR_UI_STATE);
    clearState();
  }, [clearState]);

  return {
    uiState,
    updateUIState,
    resetUIState,
    hasPersistedState: hasPersistedState(),
  };
}

export default usePlaybookEditUI;
